import api from './api';
import type { PaginatedResponse } from '@/types';

/**
 * KYC documents — the two stores, one client.
 *
 * `customerKycService` talks to `/customers/{id}/documents/`, which four
 * audiences reach: broker staff, the broker's POSPs, and the contact
 * themselves. `channelKycService` talks to `/brokers/channel-documents/`,
 * which the Broker Admin and the agent behind a channel both read — the same
 * rows, so the channel detail page and the agent's My Portal cannot disagree
 * about whether a PAN card was accepted.
 *
 * Types live here rather than in `@/types` because nothing outside this
 * feature needs them.
 */

// ─── Extraction ───────────────────────────────────────────────────────────────

/**
 * What OCR read off one document, per Customer field.
 *
 * CANDIDATES, never an answer. Tesseract on a phone photo confuses 0/O and 8/B
 * routinely, so a reading is offered for a person to confirm or correct — it
 * reaches the contact record only through `confirmExtraction`. `confidence` is
 * a coarse ordering hint (80 for a sole clean reading, 55 when there were
 * rivals), not a calibrated probability; never present it as one.
 */
export interface ExtractedField {
  candidates: string[];
  confidence: number;
}

export interface ExtractedData {
  engine?: string;
  extracted_at?: string;
  document_type?: string;
  fields?: Record<string, ExtractedField>;
}

/**
 * `not_attempted` — this document type has no fields to read (a photograph).
 * `unavailable`   — the reader could not run, or the file could not be opened.
 * `no_match`      — it ran and recognised nothing.
 * `unconfirmed`   — it read something; a person must confirm it.
 * `confirmed`     — a person accepted (or corrected) it and it was saved.
 * `discarded`     — a person rejected the reading; nothing was saved.
 *
 * The first three are deliberately distinct: "we did not try" and "we tried and
 * found nothing" are different answers and a KYC screen must not merge them.
 */
export type ExtractionStatus =
  | 'not_attempted'
  | 'unavailable'
  | 'no_match'
  | 'unconfirmed'
  | 'confirmed'
  | 'discarded';

export type DocumentStatus = 'pending' | 'verified' | 'rejected' | 'expired';

// ─── Customer KYC documents ───────────────────────────────────────────────────

export interface CustomerKycDocument {
  id: string;
  document_type: string;
  document_type_display: string;
  name: string;
  description: string;
  /**
   * B13 — the AUTHENTICATED route to the bytes, not a `/policy/media/` path.
   *
   * The payload used to carry `file`, which DRF renders as its media URL, and
   * nginx serves `/policy/media/` straight off disk with no session at all: a
   * photograph of somebody's Aadhaar card behind nothing but the link. The
   * server no longer emits that URL. Fetch through `downloadDocument` below,
   * which sends the session the same way every other API call does.
   */
  download_url: string | null;
  file_size: number | null;
  mime_type: string;
  original_filename: string;
  document_number: string;
  issue_date: string | null;
  expiry_date: string | null;
  status: DocumentStatus;
  status_display: string;
  verified_at: string | null;
  verified_by: string | null;
  verified_by_name: string;
  rejection_reason: string;
  verification_remarks: string;
  uploaded_by: string | null;
  uploaded_by_name: string;
  extracted_data: ExtractedData;
  ocr_confidence: string | null;
  extraction_status: ExtractionStatus;
  extraction_status_display: string;
  extraction_confirmed_at: string | null;
  extraction_confirmed_by: string | null;
  extraction_confirmed_by_name: string;
  created_at: string;
}

export interface ConfirmExtractionResponse {
  message: string;
  updated_fields: Record<string, string>;
  document: CustomerKycDocument;
}

/** The identity documents a contact's KYC is built from, in the order the
 *  panel lists them. The first four are the ones OCR can read; the rest are
 *  offered because brokers ask for them, and are stored without a reading. */
export const CUSTOMER_KYC_DOCUMENT_TYPES = [
  { value: 'pan', label: 'PAN Card' },
  { value: 'aadhaar_front', label: 'Aadhaar (Front)' },
  { value: 'aadhaar_back', label: 'Aadhaar (Back)' },
  { value: 'photo', label: 'Photograph' },
  { value: 'passport', label: 'Passport' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'gst_certificate', label: 'GST Certificate' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'other', label: 'Other' },
] as const;

/** Human labels for the Customer fields a reading can fill. Kept beside the
 *  document types so the confirmation panel and the KYC card name the same
 *  field the same way. */
export const KYC_FIELD_LABELS: Record<string, string> = {
  pan_number: 'PAN Number',
  aadhaar_number: 'Aadhaar Number',
  gst_number: 'GST Number',
  passport_number: 'Passport Number',
  driving_license: 'Driving License',
  voter_id: 'Voter ID',
  date_of_birth: 'Date of Birth',
  gender: 'Gender',
  pincode: 'PIN Code',
};

/**
 * Pull a KYC document's bytes through its authenticated route and hand them to
 * the browser.
 *
 * A plain `<a href>` cannot be used here: the route needs the session the API
 * client carries, and the whole point of B13 is that the bytes are no longer
 * reachable from a URL on its own. `download_url` is server-built (through
 * Django `reverse`, so it already carries the deployment's script prefix) and
 * is relative to the site, while the API client is based at the API root —
 * hence the absolute-path request rather than one relative to `api`.
 */
interface StoredKycDocument {
  download_url: string | null;
  original_filename?: string;
  name?: string;
}

async function downloadKycDocument(document: StoredKycDocument): Promise<void> {
  const blob = await fetchKycDocument(document, { inline: false });
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = url;
  link.download = document.original_filename || document.name || 'kyc-document';
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * B-03 — the SAME authenticated route, asking for a preview instead of a save.
 *
 * `?disposition=inline` is a request, not an instruction: the server honours it
 * only for the types in `apps/core/file_disposition.py`, decided from the
 * stored file's EXTENSION rather than its `mime_type` (which is whatever the
 * uploader's browser claimed, and is routinely `application/octet-stream` for a
 * perfectly ordinary PDF). Anything else still comes back as an attachment,
 * which `previewInNewTab` detects from the blob's own type and saves instead.
 *
 * Returns the bytes rather than opening anything itself: where a document is
 * shown, and what to say when it cannot be, is the panel's business.
 */
async function fetchKycDocument(document: StoredKycDocument, { inline }: { inline: boolean }): Promise<Blob> {
  if (!document.download_url) throw new Error('This document has no file stored.');
  const response = await api.get(document.download_url, {
    responseType: 'blob',
    baseURL: '/',
    ...(inline ? { params: { disposition: 'inline' } } : {}),
  });
  return response.data as Blob;
}

function previewKycDocument(document: StoredKycDocument): Promise<Blob> {
  return fetchKycDocument(document, { inline: true });
}

export const customerKycService = {
  async list(customerId: string): Promise<PaginatedResponse<CustomerKycDocument>> {
    const { data } = await api.get<PaginatedResponse<CustomerKycDocument>>(
      `/customers/${customerId}/documents/`,
      // The panel shows every document at once — it is a compliance checklist,
      // not a feed, and a paged one would silently hide the oldest. 100 is the
      // server's max_page_size; a contact past that is not a real case.
      { params: { page_size: 100 } }
    );
    return data;
  },

  async upload(
    customerId: string,
    payload: { document_type: string; file: File; name?: string }
  ): Promise<CustomerKycDocument> {
    const form = new FormData();
    form.append('document_type', payload.document_type);
    form.append('file', payload.file);
    form.append('name', payload.name || payload.file.name);
    const { data } = await api.post<CustomerKycDocument>(`/customers/${customerId}/documents/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // OCR runs in-request so the candidates are on screen when the uploader
      // is still looking at the form. The 30s client default is too tight for a
      // full-resolution phone photo on a slow uplink plus two tesseract passes.
      timeout: 180000,
    });
    return data;
  },

  async remove(customerId: string, documentId: string): Promise<void> {
    await api.delete(`/customers/${customerId}/documents/${documentId}/`);
  },

  /** Save a stored document to the viewer's device. See `downloadKycDocument`. */
  download: downloadKycDocument,

  /** Fetch a stored document's bytes for an in-browser preview (B-03). */
  preview: previewKycDocument,

  async verify(customerId: string, documentId: string, remarks: string) {
    const { data } = await api.post<{ message: string; document: CustomerKycDocument }>(
      `/customers/${customerId}/documents/${documentId}/verify/`,
      { remarks }
    );
    return data;
  },

  async reject(customerId: string, documentId: string, remarks: string) {
    const { data } = await api.post<{ message: string; document: CustomerKycDocument }>(
      `/customers/${customerId}/documents/${documentId}/reject/`,
      { remarks }
    );
    return data;
  },

  /** The ONLY path from a machine reading to the contact record. `discard` is
   *  the equally valid other outcome: the reading was wrong, save nothing. */
  async confirmExtraction(
    customerId: string,
    documentId: string,
    body: { fields?: Record<string, string>; discard?: boolean }
  ): Promise<ConfirmExtractionResponse> {
    const { data } = await api.post<ConfirmExtractionResponse>(
      `/customers/${customerId}/documents/${documentId}/confirm-extraction/`,
      body
    );
    return data;
  },
};

// ─── Channel KYC documents ────────────────────────────────────────────────────

export interface ChannelKycDocument {
  id: string;
  channel: string;
  channel_name: string;
  document_type: string;
  document_type_display: string;
  name: string;
  /** B13 — the authenticated route to the bytes. See `CustomerKycDocument.download_url`. */
  download_url: string | null;
  file_size: number | null;
  mime_type: string;
  original_filename: string;
  status: 'pending' | 'verified' | 'rejected';
  status_display: string;
  remarks: string;
  verified_by: string | null;
  verified_by_name: string;
  verified_at: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string;
  created_at: string;
}

/** The five slots the Broker Admin asked for. The first four are what the
 *  onboarding gate requires (`ChannelDocument.REQUIRED_TYPES` server-side);
 *  Aadhaar (Back) is offered but not demanded, so adding the slot does not
 *  tighten an activation rule that predates it. */
export const CHANNEL_KYC_DOCUMENT_TYPES = [
  { value: 'pan_card', label: 'PAN Card', required: true },
  { value: 'aadhaar_front', label: 'Aadhaar (Front)', required: true },
  { value: 'aadhaar_back', label: 'Aadhaar (Back)', required: false },
  { value: 'photo', label: 'Photo', required: true },
  { value: 'irdai_certificate', label: 'IRDAI Certificate', required: true },
] as const;

/** P1 — the training certificate, kept OUT of the KYC slot list above.
 *
 *  It is the same store, the same upload and the same review (one more
 *  `ChannelDocument.DOCUMENT_TYPES` entry), but it belongs to the compliance
 *  lifecycle rather than to identity KYC, and adding it to the list above
 *  would have put a sixth slot on every channel's KYC panel. `required: false`
 *  because the KYC onboarding gate (`ChannelDocument.REQUIRED_TYPES`) does not
 *  demand it -- the compliance lifecycle does, at its own stage. */
export const CHANNEL_TRAINING_DOCUMENT_TYPES = [
  { value: 'training_certificate', label: 'Training Certificate', required: false },
] as const;

export const channelKycService = {
  async list(channelId: string): Promise<PaginatedResponse<ChannelKycDocument>> {
    const { data } = await api.get<PaginatedResponse<ChannelKycDocument>>('/brokers/channel-documents/', {
      params: { channel: channelId, page_size: 100 },
    });
    return data;
  },

  async upload(payload: { channel: string; document_type: string; file: File }): Promise<ChannelKycDocument> {
    const form = new FormData();
    form.append('channel', payload.channel);
    form.append('document_type', payload.document_type);
    form.append('file', payload.file);
    form.append('name', payload.file.name);
    const { data } = await api.post<ChannelKycDocument>('/brokers/channel-documents/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
    return data;
  },

  async remove(documentId: string): Promise<void> {
    await api.delete(`/brokers/channel-documents/${documentId}/`);
  },

  /** Save a stored document to the viewer's device. See `downloadKycDocument`.
   *
   *  No `preview` twin here yet, deliberately: the channel download is served by
   *  `apps/brokers/views.py`, which still forces an attachment and is not B-03's
   *  file. Wiring it is `previewInNewTab` + one `?disposition=inline` param once
   *  that view accepts one -- see blocked_on. */
  download: downloadKycDocument,

  async verify(documentId: string, remarks: string) {
    const { data } = await api.post<{ message: string; document: ChannelKycDocument }>(
      `/brokers/channel-documents/${documentId}/verify/`,
      { remarks }
    );
    return data;
  },

  async reject(documentId: string, remarks: string) {
    const { data } = await api.post<{ message: string; document: ChannelKycDocument }>(
      `/brokers/channel-documents/${documentId}/reject/`,
      { remarks }
    );
    return data;
  },
};
